import { useState, useEffect } from 'react';
import { useMockAuth } from '@/hooks/useMockAuth';
import { useTheme } from '@/contexts/ThemeContext';
import { useVersions } from '@/hooks/api/useDownloads';
import DownloadButton from '@/components/DownloadButton';

type Platform = 'macos' | 'windows' | 'linux';

interface OwnedProduct {
  productId: string;
  productName: string;
  purchasedAt: string;
  isTrial?: boolean;
  expiresAt?: string;
}

// Mock owned products for demonstration
const MOCK_OWNED_PRODUCTS: OwnedProduct[] = [
  {
    productId: 'kaizer',
    productName: 'Kaizer',
    purchasedAt: '2024-01-01T00:00:00Z',
  },
  {
    productId: 'pedalboard',
    productName: 'PedalBoard',
    purchasedAt: '2024-01-15T00:00:00Z',
    isTrial: true,
    expiresAt: '2024-02-15T00:00:00Z',
  },
];

const DownloadsPage = () => {
  const { user } = useMockAuth();
  const { theme } = useTheme();
  const [detectedPlatform, setDetectedPlatform] = useState<Platform>('macos');
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);

  // Detect user's platform
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('win')) {
      setDetectedPlatform('windows');
    } else if (userAgent.includes('linux')) {
      setDetectedPlatform('linux');
    } else {
      setDetectedPlatform('macos');
    }
  }, []);

  const colors = {
    dark: {
      bg: '#0a0a0f',
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(102, 126, 234, 0.2)',
      text: '#ffffff',
      textMuted: '#9ca3af',
      accent: '#667eea',
      success: '#10b981',
      warning: '#f59e0b',
    },
    light: {
      bg: '#f9fafb',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      text: '#111827',
      textMuted: '#6b7280',
      accent: '#667eea',
      success: '#10b981',
      warning: '#f59e0b',
    },
  };

  const c = colors[theme];

  if (!user) {
    return (
      <div
        style={{
          minHeight: '89.7vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: c.bg,
        }}
      >
        <p style={{ color: c.textMuted, fontSize: '1.125rem' }}>
          Please log in to view your downloads
        </p>
      </div>
    );
  }

  const getPlatformIcon = (platform: Platform) => {
    switch (platform) {
      case 'macos':
        return '🍎';
      case 'windows':
        return '🪟';
      case 'linux':
        return '🐧';
    }
  };

  const getPlatformLabel = (platform: Platform) => {
    switch (platform) {
      case 'macos':
        return 'macOS';
      case 'windows':
        return 'Windows';
      case 'linux':
        return 'Linux';
    }
  };

  return (
    <div style={{ backgroundColor: c.bg, minHeight: '89.7vh' }}>
      {/* Hero Header */}
      <div
        style={{
          background:
            theme === 'dark'
              ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '4rem 2rem 3rem',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1
            style={{
              fontSize: '3rem',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '0.5rem',
            }}
          >
            Downloads
          </h1>
          <p style={{ fontSize: '1.125rem', color: 'rgba(255, 255, 255, 0.9)' }}>
            Download your purchased plugins and software
          </p>
        </div>
      </div>

      {/* Platform Detection Banner */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 2rem 0' }}>
        <div
          style={{
            background: c.cardBg,
            border: `1px solid ${c.cardBorder}`,
            borderRadius: '0.75rem',
            padding: '1rem 1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
          }}
        >
          <span style={{ fontSize: '1.5rem' }}>{getPlatformIcon(detectedPlatform)}</span>
          <div style={{ flex: 1 }}>
            <p style={{ color: c.text, fontWeight: '500', margin: 0 }}>
              Detected Platform: {getPlatformLabel(detectedPlatform)}
            </p>
            <p style={{ color: c.textMuted, fontSize: '0.875rem', margin: 0 }}>
              Downloads will default to your platform. You can also download for other platforms.
            </p>
          </div>
        </div>
      </div>

      {/* Products List */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        <h2
          style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: c.text,
            marginBottom: '1.5rem',
          }}
        >
          Your Products
        </h2>

        {MOCK_OWNED_PRODUCTS.length === 0 ? (
          <div
            style={{
              background: c.cardBg,
              border: `1px solid ${c.cardBorder}`,
              borderRadius: '1rem',
              padding: '3rem',
              textAlign: 'center',
              backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
            }}
          >
            <p style={{ color: c.textMuted, fontSize: '1.125rem' }}>
              You don't have any products yet.
            </p>
            <a
              href="/marketplace"
              style={{
                display: 'inline-block',
                marginTop: '1rem',
                padding: '0.75rem 1.5rem',
                backgroundColor: c.accent,
                color: 'white',
                borderRadius: '0.5rem',
                textDecoration: 'none',
                fontWeight: '500',
              }}
            >
              Browse Marketplace
            </a>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {MOCK_OWNED_PRODUCTS.map((product) => (
              <ProductDownloadCard
                key={product.productId}
                product={product}
                detectedPlatform={detectedPlatform}
                theme={theme}
                colors={c}
                isExpanded={selectedProduct === product.productId}
                onToggle={() =>
                  setSelectedProduct(
                    selectedProduct === product.productId ? null : product.productId
                  )
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface ProductDownloadCardProps {
  product: OwnedProduct;
  detectedPlatform: Platform;
  theme: 'dark' | 'light';
  colors: {
    bg: string;
    cardBg: string;
    cardBorder: string;
    text: string;
    textMuted: string;
    accent: string;
    success: string;
    warning: string;
  };
  isExpanded: boolean;
  onToggle: () => void;
}

function ProductDownloadCard({
  product,
  detectedPlatform,
  theme,
  colors: c,
  isExpanded,
  onToggle,
}: ProductDownloadCardProps) {
  const [selectedVersion, setSelectedVersion] = useState<string>('latest');
  const { data: versions } = useVersions(product.productId, isExpanded);

  const platforms: Platform[] = ['macos', 'windows', 'linux'];

  const getPlatformIcon = (platform: Platform) => {
    switch (platform) {
      case 'macos':
        return '🍎';
      case 'windows':
        return '🪟';
      case 'linux':
        return '🐧';
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div
      style={{
        background: c.cardBg,
        border: `1px solid ${c.cardBorder}`,
        borderRadius: '1rem',
        overflow: 'hidden',
        backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
        boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
      }}
    >
      {/* Product Header */}
      <div
        onClick={onToggle}
        style={{
          padding: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          borderBottom: isExpanded ? `1px solid ${c.cardBorder}` : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
              color: 'white',
              fontWeight: 'bold',
            }}
          >
            {product.productName.charAt(0)}
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <h3
                style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: c.text,
                  margin: 0,
                }}
              >
                {product.productName}
              </h3>
              {product.isTrial && (
                <span
                  style={{
                    padding: '0.125rem 0.5rem',
                    borderRadius: '9999px',
                    fontSize: '0.625rem',
                    fontWeight: '600',
                    backgroundColor: c.warning,
                    color: 'white',
                    textTransform: 'uppercase',
                  }}
                >
                  Trial
                </span>
              )}
            </div>
            <p style={{ color: c.textMuted, fontSize: '0.875rem', margin: 0 }}>
              Purchased {formatDate(product.purchasedAt)}
              {product.expiresAt && ` • Expires ${formatDate(product.expiresAt)}`}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <DownloadButton
            productId={product.productId}
            platform={detectedPlatform}
            version={selectedVersion}
            theme={theme}
          />
          <span
            style={{
              fontSize: '1.5rem',
              color: c.textMuted,
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            ▼
          </span>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div style={{ padding: '1.5rem' }}>
          {/* Version Selector */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              style={{
                display: 'block',
                color: c.textMuted,
                fontSize: '0.875rem',
                marginBottom: '0.5rem',
              }}
            >
              Version
            </label>
            <select
              value={selectedVersion}
              onChange={(e) => setSelectedVersion(e.target.value)}
              style={{
                width: '200px',
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                border: `1px solid ${c.cardBorder}`,
                backgroundColor: theme === 'dark' ? 'rgba(15, 15, 20, 0.6)' : '#ffffff',
                color: c.text,
                fontSize: '0.875rem',
                cursor: 'pointer',
              }}
            >
              <option value="latest">Latest</option>
              {versions?.map((v) => (
                <option key={v.version} value={v.version}>
                  {v.version} {v.isLatest ? '(Latest)' : ''} - {v.releaseDate}
                </option>
              ))}
            </select>
          </div>

          {/* Platform Downloads */}
          <div>
            <label
              style={{
                display: 'block',
                color: c.textMuted,
                fontSize: '0.875rem',
                marginBottom: '0.75rem',
              }}
            >
              Download for Platform
            </label>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {platforms.map((platform) => (
                <div
                  key={platform}
                  style={{
                    flex: '1',
                    minWidth: '200px',
                    padding: '1rem',
                    borderRadius: '0.75rem',
                    border: `1px solid ${
                      platform === detectedPlatform ? c.accent : c.cardBorder
                    }`,
                    backgroundColor:
                      platform === detectedPlatform
                        ? theme === 'dark'
                          ? 'rgba(102, 126, 234, 0.1)'
                          : 'rgba(102, 126, 234, 0.05)'
                        : 'transparent',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.75rem',
                    }}
                  >
                    <span style={{ fontSize: '1.25rem' }}>{getPlatformIcon(platform)}</span>
                    <span style={{ color: c.text, fontWeight: '500' }}>
                      {platform === 'macos'
                        ? 'macOS'
                        : platform === 'windows'
                          ? 'Windows'
                          : 'Linux'}
                    </span>
                    {platform === detectedPlatform && (
                      <span
                        style={{
                          padding: '0.125rem 0.375rem',
                          borderRadius: '4px',
                          fontSize: '0.625rem',
                          fontWeight: '600',
                          backgroundColor: c.success,
                          color: 'white',
                        }}
                      >
                        Your Platform
                      </span>
                    )}
                  </div>
                  <DownloadButton
                    productId={product.productId}
                    platform={platform}
                    version={selectedVersion}
                    theme={theme}
                    fullWidth
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DownloadsPage;
